import '../sp-image/sp-image';

import {customElement, property} from "lit/decorators.js";
import {css, html, LitElement, PropertyValues, unsafeCSS} from "lit";
import {SpFile, SpRemoteFile} from "../inputs/interfaces";
import {SpImageService} from "../../services/sp-image.service";
import {repeat} from "lit-html/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {translate, TranslateService} from "@stefanholzapfel/lit-translate";
import {mobileHelper} from "../../styles/mobile-helper.styles";
import {mobileBreakPoint, mobileBreakpointNumber} from "../../styles/sizes";
import {SpPhotoswipeController} from "../../controllers/sp-photoswipe.controller";
import {getIcon} from "../../styles/icons";

// TODO: sp-gallery display thumbs

@customElement('sp-image-gallery')
export class SpImageGallery extends LitElement {
    static styles = [
        mobileHelper,
        css`:host {
                display: block;
                position: relative;
            }

            .label {
                position: absolute;
                bottom: 0.3rem;
                left: 0.3rem;
                color: white;
                font-size: 12px;
                text-shadow: 0 0 3px black;
            }

            sp-image {
                transition: transform .5s;
            }

            sp-image:hover {
                transform: scale3d(1.1, 1.1, 1);
            }

            .thumbs {
                display: grid;
                grid-auto-flow: column;
                grid-gap: calc(var(--sp-default-padding) / 2);
                transition: opacity 100ms linear;
                width: 100%;
            }

            .thumbs sp-image::part(spinner) {
                --track-width: 0.3rem;
                font-size: 3rem;
            }

            .thumb {
                width: var(--sp-default-icon-size);
                height: var(--sp-default-icon-size);
                cursor: zoom-in;
                border-radius: var(--sp-default-border-radius);
                overflow: hidden;
            }
            
            .hide-overflow {
                overflow: hidden;
            }

            @media (min-width: ${unsafeCSS(mobileBreakPoint)}) {
                .wrapper, .main, .root {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    cursor: zoom-in;
                }

                .root {
                    position: absolute;
                    left: 1rem;
                    top: 1rem;
                    height: 6.5rem;
                    width: 6.5rem;
                    border: 2px solid white;
                    z-index: 2;
                    transition: opacity 100ms linear;
                }

                .thumbs {
                    position: absolute;
                    bottom: 4rem;
                    place-content: center;
                }
                
                .thumb {
                    border: 2px solid rgba(255, 255, 255, 0.73);
                }
            }

            @media (max-width: ${unsafeCSS(mobileBreakPoint)}) {
                .wrapper {
                    display: grid;
                    width: 100%;
                    grid-auto-flow: column;
                    grid-gap: 2px;
                    aspect-ratio: 1;
                }

                /* This is a best effort approach for legacy browsers where heights are hardcoded */
                @supports not (aspect-ratio: 1) {
                    .wrapper {
                        height: 16rem;
                    }
                }

                .wrapper :first-child {
                    border-top-left-radius: var(--sp-default-border-radius);
                    border-bottom-left-radius: var(--sp-default-border-radius);
                }

                .wrapper :last-child {
                    border-top-right-radius: var(--sp-default-border-radius);
                    border-bottom-right-radius: var(--sp-default-border-radius);
                }

                .thumbs {
                    padding-top: var(--sp-default-padding-mobile);
                    grid-column: 1 / 3;
                    grid-template-columns: 1fr 1fr 1fr 1fr;
                    width: 100%;
                }

                .thumb {
                    width: 100%;
                    height: 100%;
                    aspect-ratio: 1;
                }

                /* This is a best effort approach for legacy browsers where heights are hardcoded */
                @supports not (aspect-ratio: 1) {
                    .thumb {
                        height: var(--sp-default-icon-size);
                    }
                }

                .main, .root {
                    position: relative;
                    cursor: zoom-in;
                }
            }`
    ];

    // Used to distinguish between galleries when multiple instances exist on a page
    @property({type: String})
    id: string;

    @property({type: Array})
    rootImage: SpFile | SpRemoteFile;

    // The images for the gallery. If special behavior is needed for images, they can provide a clickHandler with custom logic
    @property({type: Array})
    images: ((SpFile | SpRemoteFile) & { clickHandler?: (openImage: Function) => void })[] = [];

    // Suppress attachment of a searchParam to the route on opening gallery.
    @property({type: Boolean})
    suppressUrlState = false;

    photoswipeController: SpPhotoswipeController;

    close() {
        this.photoswipeController.close();
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        this.photoswipeController = new SpPhotoswipeController(this, {
            suppressUrlState: this.suppressUrlState,
            openFromElement: this.getImageThumbElement.bind(this),
            hideOnOpen: this.getHideOnOpenElements.bind(this),
            ...this.id && { galleryId: this.id }
        });
        this.photoswipeController.imageGallery.on('uiRegister', () => {
            this.photoswipeController.imageGallery.pswp.ui.registerElement({
                name: 'root_label',
                appendTo: 'wrapper',
                onInit: (el, pswp) => {
                    const label = document.createElement('div');
                    label.textContent = TranslateService.translate('DETAIL_VALUE_ROOT').toString();
                    el.appendChild(label);
                    el.style.display = 'grid';
                    el.style.placeContent = 'center';
                    el.style.color = 'white';
                    el.style.fontSize = '2rem';
                    el.style.height = '30px';
                    el.style.marginTop = '15px';
                    pswp.on('change', (a,) => {
                        if (this.rootImage && pswp.currIndex === pswp.getNumItems() - 1)
                            el.style.opacity = '1';
                        else
                            el.style.opacity = '0';
                    });
                }
            });
        });
    }

    protected async updated(_changedProperties: PropertyValues) {
        super.updated(_changedProperties);
        if (_changedProperties.has('images') || _changedProperties.has('rootImage')) {
            const imgArray = [
                ...(Array.isArray((this.images)) ? this.images : []),
                ...(this.rootImage ? [this.rootImage] : [])
            ];
            this.photoswipeController.updateData(imgArray.map(img=> {
                return {
                    fullSize: SpImageService.getUrlOrBlobFromImage(img),
                    thumb: SpImageService.getUrlOrBlobFromImage(img)
                }
            }));
            this.requestUpdate();
        }
    }

    getImageThumbElement(index: number) {
        return index === (this.images.length) ?
            this.shadowRoot.querySelector(`.root`) :
            this.shadowRoot.getElementById(`thumb-${index}`);
    }

    getHideOnOpenElements(index: number) {
        const desktopView = window.innerWidth > mobileBreakpointNumber;
        if (index === 0 && desktopView) {
            const toHide = [];
            const rootThumb = this.shadowRoot.querySelector(`.root`);
            const thumbs = this.shadowRoot.querySelector(`.thumbs`);
            if (rootThumb)
                toHide.push(rootThumb);
            if (thumbs)
                toHide.push(thumbs);
            return toHide;
        }
        return [];
    }

    protected render() {
        return html`
            <div class="wrapper">
                ${ when(this.rootImage, () => html`
                    <div class="hide-overflow root">
                        <sp-image
                                class="full-size"
                                @click=${ () => { this.photoswipeController.open(this.images.length) } }
                                .image=${ SpImageService.getUrlOrBlobFromImage(this.rootImage) }
                                .placeholderImage=${ SpImageService.getPlaceholderUrl('spots') } >
                        </sp-image>
                        <div class="label">
                            ${ translate('DETAIL_VALUE_ROOT') }
                        </div>
                    </div>
                `)}
                <div id="thumb-0" class="hide-overflow main">
                    <sp-image
                            .image=${ SpImageService.getUrlOrBlobFromImage(this.images[0], 'x550') }
                            .placeholderImage=${ SpImageService.getPlaceholderUrl('spots') }
                            @click=${ () => { this.handleImageClick(0) } }>
                    </sp-image>
                    <slot></slot>
                    ${ when(!!this.rootImage, () => html`
                        <div class="label mobile-only">
                            ${ translate('DETAIL_VALUE_UPDATE') }
                        </div>`) }
                </div>
            </div>
            ${ when(this.images.length > 1, () => html`
                <div class="thumbs">
                    ${ repeat(this.images, image => image.spec.id, (image, index) =>
                            {
                                if (index > 0 && (index < 4 || (index === 4 && this.images.length <= 5)))
                                    return html`
                                            <sp-image
                                                .showSpinner=${false}
                                                id="thumb-${ index }"
                                                @click=${ () => { this.handleImageClick(index) } }
                                                class="thumb"
                                                .image=${ SpImageService.getUrlOrBlobFromImage(image, 'x550') }
                                                .placeholderImage=${ SpImageService.getPlaceholderUrl('spots') }>
                                            </sp-image>`
                            }
                    )}
                ${ when(this.images.length > 5, () => html`
                    <sp-button
                            class="thumb"
                            @click=${ () => { this.handleImageClick(4) } }
                            .icon=url(${getIcon('ellipsis-v-square', { size: "x128" })}></sp-button>
                `) }
                </div>`) }`;
    }

    handleImageClick(index: number) {
        if (this.images[index].clickHandler) {
            this.images[index].clickHandler(() => {
                this.photoswipeController.open(index);
            });
        } else {
            this.photoswipeController.open(index);
        }
    }
}
